import React, { useState, useMemo } from "react";
import Select from "react-select";
import { Form } from "react-bootstrap";
import {
  OPERATOR_OPTIONS,
  getValueInputType,
} from "../../utils/tsaConditionOption";
import styles from "./index.module.css";

export const TSAConditionRow = ({
  columns,
  condition,
  onChange,
  errors = {}, // New prop for field-specific errors
}) => {
  const [selectedColumn, setSelectedColumn] = useState(
    condition.columnId || null
  );
  const [operator, setOperator] = useState(condition.operator || null);
  const [values, setValues] = useState(condition.values || []);
  const [valueRange, setValueRange] = useState(condition.valueRange || []);

  const selectedDataType = useMemo(() => {
    const col = columns.find((c) => c._id === selectedColumn);
    return col?.dataType || null;
  }, [selectedColumn, columns]);

  const operatorOptions = selectedDataType
    ? OPERATOR_OPTIONS[selectedDataType]
    : [];

  const valueInputType =
    selectedDataType && operator
      ? getValueInputType(selectedDataType, operator)
      : null;

  const handleColumnChange = (e) => {
    const newColumnId = e.value;
    setSelectedColumn(newColumnId);
    setOperator(null);
    setValues([]);
    setValueRange([]);
    onChange({
      ...condition,
      columnId: newColumnId,
      operator: null,
      values: [],
      valueRange: [],
      dataType: columns.find((c) => c._id === newColumnId)?.dataType || "",
    });
  };

  const handleRangeValueChange = (index, newValue) => {
    const updatedRange = [...valueRange];
    updatedRange[index] = newValue?.trim?.() || newValue || "";
    setValueRange(updatedRange);
    onChange({ ...condition, valueRange: updatedRange, values: [] });
  };

  const handleOperatorChange = (e) => {
    const newOperator = e.value;
    setOperator(newOperator);
    setValues([]);
    onChange({
      ...condition,
      operator: newOperator,
      values: [],
      dataType: columns.find((c) => c._id === selectedColumn)?.dataType || "",
    });
  };

  const handleValueChange = (e) => {
    let newValues = [];

    if (Array.isArray(e)) {
      newValues = e.map((v) => String(v.value).trim());
    } else if (e?.target) {
      const inputValue = e.target.value;
      if (inputValue === "") {
        newValues = [];
      } else if (inputValue.includes(",")) {
        newValues = inputValue.split(",").map((v) => v.trim());
      } else {
        newValues = [inputValue.trim()];
      }
    } else if (typeof e === "string") {
      if (e.includes(",")) {
        newValues = e.split(",").map((v) => v.trim());
      } else {
        newValues = [e.trim()];
      }
    } else if (typeof e === "object" && e?.value !== undefined) {
      newValues = [String(e.value).trim()];
    }

    // For date-range or number-range
    if (valueInputType === "date-range" || valueInputType === "number-range") {
      newValues = e; // Expecting array [start, end]
    }

    setValues(newValues);
    onChange({ ...condition, values: newValues });
  };

  return (
    <div className={styles.tsaConditionRow}>
      {/* Column Select */}
      <div className={styles.tsaColumnSelect}>
        <Form.Label>Select Column</Form.Label>
        <Select
          placeholder="Select Column"
          value={
            selectedColumn
              ? {
                  value: selectedColumn,
                  label: columns.find((c) => c._id === selectedColumn)?.label,
                }
              : null
          }
          options={columns.map((col) => ({
            label: col.label,
            value: col._id,
          }))}
          onChange={handleColumnChange}
          styles={{
            control: (base) => ({
              ...base,
              borderColor: errors.columnId ? "red" : base.borderColor,
            }),
          }}
        />
        {errors.columnId && (
          <Form.Text className="text-danger">{errors.columnId}</Form.Text>
        )}
      </div>

      {/* Operator Select */}
      <div className={styles.tsaOperatorSelect}>
        <Form.Label>Select Operator</Form.Label>
        <Select
          placeholder="Select Operator"
          value={
            operator
              ? operatorOptions.find((opt) => opt.value === operator) || null
              : null
          }
          options={operatorOptions}
          onChange={handleOperatorChange}
          isDisabled={!selectedDataType}
          styles={{
            control: (base) => ({
              ...base,
              borderColor: errors.operator ? "red" : base.borderColor,
            }),
          }}
        />
        {errors.operator && (
          <Form.Text className="text-danger">{errors.operator}</Form.Text>
        )}
      </div>

      {/* Value Input */}
      <div className={styles.tsaValueInput}>
        <Form.Label>Condition Values</Form.Label>
        {valueInputType === "boolean" ? (
          <Select
            placeholder="Select Value"
            value={
              values.length
                ? [
                    { label: "True or Yes", value: true },
                    { label: "False or No", value: false },
                  ].find((opt) => String(opt.value) === String(values[0])) ||
                  null
                : null
            }
            options={[
              { label: "True or Yes", value: true },
              { label: "False or No", value: false },
            ]}
            onChange={handleValueChange}
            isDisabled={!operator}
            styles={{
              control: (base) => ({
                ...base,
                borderColor: errors.values ? "red" : base.borderColor,
              }),
            }}
          />
        ) : valueInputType === "date-range" ? (
          <div className={styles.tsaRangeGroup}>
            <Form.Control
              type="date"
              value={valueRange[0] || ""}
              placeholder="Start Date"
              onChange={(e) => handleRangeValueChange(0, e.target.value)}
              className={styles.tsaRangeInput}
              style={{ borderColor: errors.values ? "red" : undefined }}
            />
            <Form.Control
              type="date"
              value={valueRange[1] || ""}
              placeholder="End Date"
              onChange={(e) => handleRangeValueChange(1, e.target.value)}
              className={styles.tsaRangeInput}
              style={{ borderColor: errors.values ? "red" : undefined }}
            />
          </div>
        ) : valueInputType === "date" ? (
          <Form.Control
            type="date"
            value={values[0] || ""}
            onChange={(e) => handleValueChange(e.target.value)}
            className={styles.tsaInput}
            style={{ borderColor: errors.values ? "red" : undefined }}
          />
        ) : valueInputType === "number-range" ? (
          <div className={styles.tsaRangeGroup}>
            <Form.Control
              type="number"
              value={valueRange[0] || ""}
              placeholder="Start Value"
              onChange={(e) => handleRangeValueChange(0, e.target.value)}
              className={styles.tsaRangeInput}
              style={{ borderColor: errors.valueRange ? "red" : undefined }}
            />
            <Form.Control
              type="number"
              value={valueRange[1] || ""}
              placeholder="End Value"
              onChange={(e) => handleRangeValueChange(1, e.target.value)}
              className={styles.tsaRangeInput}
              style={{ borderColor: errors.valueRange ? "red" : undefined }}
            />
          </div>
        ) : valueInputType == "number" ? (
          <Form.Control
            type="number"
            value={values[0] || ""}
            onChange={(e) => handleValueChange(e.target.value)}
            placeholder="e.g. 1000"
            className={styles.tsaInput}
            style={{ borderColor: errors.values ? "red" : undefined }}
          />
        ) : (
          <Form.Control
            type="text"
            value={values.join(", ")}
            onChange={handleValueChange}
            className={styles.tsaInput}
            placeholder="e.g. residential , premium"
            style={{ borderColor: errors.values ? "red" : undefined }}
          />
        )}
        {errors.values && (
          <Form.Text className="text-danger">{errors.values}</Form.Text>
        )}
      </div>
    </div>
  );
};
